import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SyncStatus } from "@/components/SyncStatus";
import { TeamPicker } from "@/components/teams/TeamPicker";
import { TeamHeader } from "@/components/teams/TeamHeader";
import { OverviewTab } from "@/components/teams/OverviewTab";
import { DepthTab, InjuriesTab, NewsTab, RosterTab, ScheduleTab, StatsTab, TransactionsTab } from "@/components/teams/TeamTabs";
import "@/components/teams/teams.css";

const TABS = [
  ["overview", "Overview", OverviewTab], ["schedule", "Schedule", ScheduleTab], ["roster", "Roster", RosterTab],
  ["stats", "Stats", StatsTab], ["depth", "Depth Chart", DepthTab], ["injuries", "Injuries", InjuriesTab],
  ["transactions", "Transactions", TransactionsTab], ["news", "News", NewsTab],
];
const LAST = "teams-last";

export default function Teams() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [page, setPage] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("overview");
  const { user } = useAuth();
  const [syncing, setSyncing] = useState(false);

  const refreshNow = async () => {
    setSyncing(true);
    const before = page?.last_sync;
    try {
      await api.post("/teams/sync");
      toast.success("Ξεκίνησε η ανανέωση δεδομένων…");
      for (let i = 0; i < 24; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const r = await api.get(`/teams/${id}`);
        setPage(r.data);
        if (r.data.last_sync && r.data.last_sync !== before) { toast.success("Τα δεδομένα ανανεώθηκαν"); break; }
      }
      api.get("/teams").then((r) => setTeams(r.data)).catch(() => {});
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => { api.get("/teams").then((r) => setTeams(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!teams.length) return;
    const last = localStorage.getItem(LAST);
    if (!id) navigate(`/teams/${teams.some((t) => t.id === last) ? last : teams[0].id}`, { replace: true });
    else if (!teams.some((t) => t.id === id)) navigate(`/teams/${teams[0].id}`, { replace: true });
  }, [id, teams, navigate]);
  useEffect(() => {
    if (!id) return;
    localStorage.setItem(LAST, id);
    setPage(null); setError("");
    api.get(`/teams/${id}`).then((r) => setPage(r.data)).catch((e) => setError(errorText(e)));
  }, [id]);

  const Tab = (TABS.find((t) => t[0] === tab) || TABS[0])[2];
  return (
    <div className="tm-root" data-testid="teams-page">
      <TeamPicker teams={teams} selected={id} onPick={(tid) => navigate(`/teams/${tid}`)} />
      <main className="tm-main">
        {error && <p className="tm-empty" data-testid="teams-error">{error}</p>}
        {!page && !error && <p className="tm-empty" data-testid="teams-loading">Loading team…</p>}
        {page && (
          <>
            <TeamHeader page={page} />
            <div className="tm-sync-row">
              <SyncStatus lastSync={page.last_sync} onRefresh={refreshNow} busy={syncing} canRefresh={user?.role === "admin"} testid="teams-sync" />
            </div>
            <nav className="tm-tabs" data-testid="teams-tabs">
              {TABS.map(([key, label]) => (
                <button key={key} type="button" className={tab === key ? "is-active" : ""} onClick={() => setTab(key)} data-testid={`teams-tab-${key}`}>{label}</button>
              ))}
            </nav>
            <Tab page={page} onTab={setTab} />
          </>
        )}
      </main>
    </div>
  );
}
